import { onAuthStateChanged, signInAnonymously, type User } from 'firebase/auth';
import { firebaseAuth } from './app';

/**
 * Anonymous sign-in gives the guest a stable Firebase UID for the browser
 * session without asking for any personal detail. The UID alone grants nothing:
 * authority to call a specific room comes from the server-written guest session
 * (`registerGuestSession`), not from being signed in.
 */
export async function ensureAnonymousUser(): Promise<User> {
  const auth = firebaseAuth();

  if (auth.currentUser) return auth.currentUser;

  // Firebase restores a persisted session asynchronously; waiting for the first
  // auth-state event avoids minting a second anonymous UID on every reload.
  const restored = await new Promise<User | null>((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user);
    });
  });

  if (restored) return restored;

  const credential = await signInAnonymously(auth);
  return credential.user;
}

export function watchAuthState(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(firebaseAuth(), callback);
}
